(() => {
  if (customElements.get('mw-pilot-preview')) return;
  const ids = value => Array.isArray(value) ? value.map(String) : [];
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  class ProductCard extends HTMLElement {
    connectedCallback() {
      this.onReady = () => this.initialize();
      this.onSection = () => this.refresh();
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', this.onReady, {once:true});
      else queueMicrotask(this.onReady);
      document.addEventListener('shopify:section:load', this.onSection);
      document.addEventListener('mw:compare-ready', this.onSection);
    }
    disconnectedCallback() {
      document.removeEventListener('DOMContentLoaded', this.onReady);
      document.removeEventListener('shopify:section:load', this.onSection);
      document.removeEventListener('mw:compare-ready', this.onSection);
    }
    read(selector, fallback) {
      try {return JSON.parse(this.querySelector(selector)?.textContent || JSON.stringify(fallback));}
      catch {return fallback;}
    }
    initialize() {
      if (this.initialized) return this.refresh();
      const native = this.read('[data-mw-native]', []);
      if (!native.length) return;
      this.locale = this.dataset.locale || 'en';
      this.language = this.locale.split('-')[0];
      this.currency = this.dataset.currency;
      this.labels = this.read('[data-mw-labels]', {});
      this.labels.regulated = ({"pl":"Regulacja nóg","en":"Leg adjustment","de":"Verstellbare Beine","fr":"Pieds réglables","es":"Patas regulables","nl":"Verstelbare poten"})[this.language] || this.labels.regulated;
      this.canonical = this.read('[data-mw-canonical]', {});
      this.preview = this.dataset.draftMode === 'true';
      const componentCatalog = this.read('[data-mw-components]', {});
      this.data = this.preview ? this.read('[data-mw-draft]', {}) : componentCatalog.items || {};
      for (const [id, item] of Object.entries(this.data)) {
        const localized = item.i18n?.[this.language] || item.i18n?.en || {};
        this.data[id] = this.normalize({...item, ...localized});
      }
      for (const row of native) {
        const id = String(row.id), saved = this.data[id];
        let item = {...saved, ...row};
        // Resolved Shopify records take precedence over the saved preview snapshot.
        this.data[id] = this.normalize(item);
      }
      this.current = this.data[this.dataset.productId];
      if (!this.preview && this.current && componentCatalog.contents?.[this.current.id]) this.current.contents = componentCatalog.contents[this.current.id];
      this.root = document.getElementById('ProductSection-' + this.dataset.sectionId + '-' + this.dataset.productId)
        || [...document.querySelectorAll('.product-section')].find(node => node.dataset.productId === this.dataset.productId && node.dataset.sectionId === this.dataset.sectionId);
      if (!this.root || !this.current) return;
      // The original theme forces every lightbox image to 50% of its source size.
      // Fit the initial zoom only for this pilot on narrow viewports; preserve zoom/pan.
      const photoswipe = window.theme?.Photoswipe?.prototype;
      if (photoswipe && !photoswipe.mwPilotZoomFit) {
        const originalGetImageData = photoswipe.getImageData;
        photoswipe.getImageData = function(...args) {
          const items = originalGetImageData.apply(this, args);
          const isPilot = this.container?.closest('.product-section')?.querySelector('mw-pilot-preview');
          const width = document.documentElement.clientWidth;
          if (isPilot && width < 769) {
            const height = Math.max(1, window.innerHeight - 120);
            for (const item of items) {
              const w = Number(item.w), h = Number(item.h);
              if (w > 0 && h > 0) item.initialZoomLevel = Math.min(0.5, width / w, height / h);
            }
          }
          return items;
        };
        photoswipe.mwPilotZoomFit = true;
      }
      this.initialized = true;
      this.renderPickers();
      this.renderContents();
      this.renderAccessories();
      this.renderDescription();
      for (const node of this.root.querySelectorAll('[data-mw-colour-launch]')) node.remove();
      const fixes = [{"locale":"pl","resourceId":"gid://shopify/Metaobject/411971256701","resourceField":"answer","rendering":"Replace only this exact substring in rendered text/HTML","old":"Wszystkie produkty powstają jw Polsce","new":"Wszystkie produkty powstają w Polsce","reason":"Literówka, bez zmiany sensu."},{"locale":"nl","resourceId":"gid://shopify/Metaobject/507040366973","resourceField":"title","rendering":"Exact text replacement","old":"Plaats de aansteker.","new":"Plaats de aanmaakblokjes of aanmaakwol.","plMeaning":"Ułóż kostki rozpałki lub wełnę rozpałkową.","reason":"Aansteker to zapalniczka; krok opisuje układanie stałej rozpałki."},{"locale":"nl","resourceId":"gid://shopify/Metaobject/507040563581","resourceField":"description","rendering":"Exact text replacement","old":"Steek de aansteker aan en laat het vuur zijn werk doen.","new":"Steek de aanmaakblokjes of aanmaakwol aan en laat het vuur zijn werk doen.","plMeaning":"Podpal kostki rozpałki lub wełnę rozpałkową i pozwól ogniowi się rozpalić.","reason":"Przedmiotem zapalania jest rozpałka, nie zapalniczka."},{"locale":"es","resourceId":"gid://shopify/Metaobject/507040366973","resourceField":"title","rendering":"Exact text replacement","old":"Coloca el encendedor.","new":"Coloca las pastillas o la lana de encendido.","plMeaning":"Ułóż kostki rozpałki lub wełnę rozpałkową.","reason":"Encendedor bez doprecyzowania oznacza zapalniczkę; materiał rozpałkowy jest wskazany w source EN."},{"locale":"es","resourceId":"gid://shopify/Metaobject/507040366973","resourceField":"description","rendering":"Exact text replacement","old":"Coloca encima unos cuantos cubos de encendido o trozos de lana de arranque, distribuyéndolos de forma uniforme.","new":"Coloca encima unas pastillas de encendido o unos trozos de lana de encendido, distribuyéndolos de forma uniforme.","plMeaning":"Na wierzchu ułóż kilka kostek rozpałki lub kawałków wełny rozpałkowej, rozkładając je równomiernie.","reason":"Naturalna terminologia pastillas de encendido/lana de encendido; zachowany sens source EN."},{"locale":"es","resourceId":"gid://shopify/Metaobject/507040563581","resourceField":"description","rendering":"Exact text replacement","old":"Enciende el iniciador y deja que el fuego haga su trabajo.","new":"Enciende las pastillas o la lana de encendido y deja que el fuego haga su trabajo.","plMeaning":"Podpal kostki rozpałki lub wełnę rozpałkową i pozwól ogniowi się rozpalić.","reason":"Spójne określenie rozpałki względem poprzedniego kroku; znaczenie zgodne ze źródłem."},{"locale":"nl","resourceId":"gid://shopify/Metaobject/510708482429","resourceField":"title","old":"Pelletvuur – schoonheid en gemak","new":"Pelletvuur – reinheid en gemak","reason":"Preserve EN cleanliness meaning in the isolated two-product renderer."}].filter(row => row.locale === this.language);
      const walker = document.createTreeWalker(document.querySelector('main') || document.body, NodeFilter.SHOW_TEXT);
      let node; while ((node = walker.nextNode())) {
        if (node.parentElement?.closest('script,style')) continue;
        for (const fix of fixes) if (node.nodeValue.includes(fix.old)) node.nodeValue=node.nodeValue.replaceAll(fix.old,fix.new);
      }
      this.refresh();
    }
    normalize(item) {
      item.id = String(item.id);
      item.variantId = String(item.variantId || '');
      for (const key of ['colors','bundles','contents','accessories','sizes','bonus']) item[key] = ids(item[key]);
      item.compare = String(item.compare || '');
      if (typeof item.quantities === 'string') {
        try {item.quantities = JSON.parse(item.quantities);} catch {item.quantities = {};}
      }
      item.quantities ||= {};
      item.specs ||= {};
      item.short ||= item.title || "";
      return item;
    }
    price(item) {
      if (item.currency === this.currency && Number.isFinite(Number(item.price))) return {amount:Number(item.price), currency:this.currency};
      if (Number.isFinite(Number(item.prices?.[this.currency]))) return {amount:Number(item.prices[this.currency]), currency:this.currency};
      return {amount:Number(item.price || 0), currency:item.currency || this.currency};
    }
    money(item) {
      const price = this.price(item);
      return new Intl.NumberFormat(this.locale, {style:'currency',currency:price.currency}).format(price.amount/100);
    }
    currencyMoney(amount) {
      return new Intl.NumberFormat(this.locale, {style:'currency',currency:this.currency}).format(amount/100);
    }
    href(item) {
      const root = (this.dataset.rootUrl || '/').replace(/\/$/,'');
      const localizedPath = source => {
        const match = source.pathname.match(/\/(products|products_preview)(?:\/|$)/);
        return root + (match ? source.pathname.slice(match.index) : '/products/' + item.handle);
      };
      if (this.dataset.editorMode === 'true') return root + '/products/' + item.handle;
      if (this.preview && item.status === 'DRAFT' && item.previewUrl) {
        const source = new URL(item.previewUrl, location.origin);
        const target = new URL(localizedPath(source) + source.search, location.origin);
        target.searchParams.set('preview_theme_id', this.dataset.theme);
        const context = new URL(location.href).searchParams;
        for (const key of ['osectx','oseid']) if (context.has(key)) target.searchParams.set(key, context.get(key));
        return target.pathname + target.search;
      }
      const target = new URL(item.url || '/products/' + item.handle, location.origin);
      target.pathname = localizedPath(target);
      if (this.preview) target.searchParams.set('preview_theme_id', this.dataset.theme);
      return target.toString();
    }
    link(item, text, className) {
      if (!this.preview && item.status === 'DRAFT') {
        const label = element('span', className, text); label.title = item.title; return label;
      }
      const node = element('a', className, text);
      node.href = this.href(item);
      node.title = item.title;
      node.dataset.relatedProductId = item.id;
      node.setAttribute('aria-label', item.title);
      if (item.id === this.current.id) node.setAttribute('aria-current','page');
      return node;
    }
    picture(item, url) {
      const wrapper = element('image-element');
      const picture = element('img');
      const source = new URL(url || item.image, location.origin);
      if (source.hostname === 'cdn.shopify.com' || source.pathname.includes('/cdn/shop/')) source.searchParams.set('width', url ? '900' : '600');
      picture.src = source.toString();
      picture.alt = item.title;
      picture.width = 360; picture.height = 360; picture.loading = 'lazy';
      wrapper.append(picture);
      return wrapper;
    }
    swatch(item, className) {
      const node = element('span', className || 'color-swatch');
      if (item.swatchImage) node.style.backgroundImage = 'url("' + item.swatchImage + '")';
      else if (item.swatch) node.style.background = item.swatch;
      return node;
    }
    items(list) {
      return [...new Set(ids(list))].map(id => this.data[id]).filter(Boolean);
    }
    quantity(product, component) {
      return Math.max(1, Math.floor(Number(product.quantities?.[component.id] || product.quantities?.['gid://shopify/Product/' + component.id] || 1)));
    }
    componentTotal(product) {
      const list = this.items(product.contents);
      if (!list.length || list.length !== product.contents.length || list.some(item => this.price(item).currency !== this.currency)) return null;
      return list.reduce((sum,item) => sum + this.price(item).amount*this.quantity(product,item),0);
    }
    optional(product) {
      const giftActive = this.dataset.giftEnabled === 'true' && (!this.dataset.giftEnd || Date.now() < Date.parse(this.dataset.giftEnd));
      const excluded = new Set([product.id, ...product.contents, ...(giftActive ? product.bonus : [])]);
      return this.items([...product.accessories, ...product.bonus]).filter(item => !excluded.has(item.id) && !item.contents.some(id => excluded.has(id)));
    }
    renderPickers() {
      const colors = this.root.querySelector('.product-variants-container--colors');
      if (colors) {
        const nodes = this.items(this.current.colors).map(item => {
          const box = element('div','product-variant' + (item.id === this.current.id ? ' active' : ''));
          box.dataset.label = item.color; box.dataset.relatedProductId = item.id;
          const link = this.link(item); link.title = item.color; link.setAttribute('aria-label',item.color);
          link.append(this.swatch(item)); box.append(link);
          this.bindLabel(box,'[data-current-label]',item.color,this.current.color);
          return box;
        });
        colors.replaceChildren(...nodes);
      }
      const bundles = this.root.querySelector('.product-block--bundles .bundles-container');
      if (bundles) {
        const nodes = this.items(this.current.bundles).map(item => {
          const box = element('div','bundles-container__item' + (item.id === this.current.id ? ' active' : ''));
          box.dataset.relatedProductId = item.id;
          const frame = element('div','bundles-container__img'); frame.dataset.productTitle = item.short;
          const link = this.link(item); if (item.image) link.append(this.picture(item)); frame.append(link); box.append(frame);
          const total = this.componentTotal(item), own = this.price(item);
          let savings = total !== null && own.currency === this.currency ? total-own.amount : 0;
          if (!savings && item.currency === this.currency && item.compareAt > item.price) savings = item.compareAt-item.price;
          if (savings > 0) {
            const label = element('p','price price--save-label');
            const body = element('span','product__price on-sale product__savings');
            body.append(element('span','price--save-label__prefix',this.labels.saving + ' '),element('span','price--save-label__amount',this.currencyMoney(savings)));
            label.append(body);box.append(label);
          }
          this.bindLabel(frame,'[data-current-item-title]',item.short,this.current.short);
          return box;
        });
        bundles.replaceChildren(...nodes);
        const label=this.root.querySelector('[data-current-item-title]');if(label) label.textContent=this.current.short;
      }
      const sizes=this.root.querySelector('.product-variants-container--size');
      if (sizes) {
        // Match Black's nominal model labels; retain exact dimensions in specifications.
        const sizeLabel = item => item.model === '600' ? '60 cm' : item.model === '800' ? '80 cm' : item.specs.pit || item.short;
        const currentSizeLabel = sizeLabel(this.current);
        const nodes=this.items([this.current.id,...this.current.sizes]).map(item => {
          const label=sizeLabel(item);
          const box=element('div','product-variant product-variant--size' + (item.id === this.current.id ? ' active' : ''));
          box.dataset.relatedProductId=item.id;box.dataset.label=label;
          const link=this.link(item);link.append(element('span','color-swatch color-swatch--text',label));box.append(link);
          this.bindLabel(box,'[data-current-size-label]',label,currentSizeLabel);return box;
        });
        sizes.replaceChildren(...nodes);
        const currentLabel=this.root.querySelector('[data-current-size-label]');if(currentLabel)currentLabel.textContent=currentSizeLabel;
      }
    }
    bindLabel(node, selector, hover, original) {
      const label=this.root.querySelector(selector);if(!label)return;
      node.addEventListener('mouseenter',()=>{label.textContent=hover;});
      node.addEventListener('mouseleave',()=>{label.textContent=original;});
      node.addEventListener('focusin',()=>{label.textContent=hover;});
      node.addEventListener('focusout',()=>{label.textContent=original;});
    }
    renderContents() {
      const items=this.items(this.current.contents);
      if(!items.length)return;
      let box=this.root.querySelector('.bundle-contents');
      if(!box){box=element('div','bundle-contents');this.root.querySelector('.product-block--desc')?.after(box);}
      const count=items.reduce((sum,item)=>sum+this.quantity(this.current,item),0);
      const heading=element('h5',null,this.labels.included+': ');
      heading.append(element('span','bundle-contents__count',this.labels.item_count.replace('__COUNT__',String(count))));
      const children=[heading],total=this.componentTotal(this.current),own=this.price(this.current);
      if(total!==null && own.currency===this.currency){
        const summary=element('div','bundle-contents__summary');
        summary.append(element('span','bundle-contents__component-total',this.labels.component_total+': '+this.currencyMoney(total)));
        if(total>own.amount)summary.append(element('span','bundle-contents__savings',this.labels.saving+': '+this.currencyMoney(total-own.amount)));
        children.push(summary);
      }
      const track=element('div','bundle-contents__scroll bundles-container');
      items.forEach((item,index)=>{
        if(index) {const plus=element('div','bundle-contents__plus','+');plus.setAttribute('aria-hidden','true');track.append(plus);}
        const quantity=this.quantity(this.current,item),prefix=quantity>1?quantity+' × ':'';
        const card=element('div','bundles-container__item bundle-contents__item');card.dataset.relatedProductId=item.id;card.dataset.quantity=String(quantity);
        const frame=element('div','bundles-container__img'),imageLink=this.link(item);if(item.image)imageLink.append(this.picture(item));frame.append(imageLink);
        const info=element('div','bundle-contents__info');info.append(this.link(item,prefix+item.short,'bundle-contents__title'),element('span','bundle-contents__price',prefix+this.money(item)));
        card.append(frame,info);track.append(card);
      });
      children.push(track);box.replaceChildren(...children);
    }
    renderAccessories() {
      const component=this.root.querySelector('mw-pilot-accessories');if(!component)return;
      const list=this.optional(this.current),track=component.querySelector('.mw-pilot-accessories__track');
      const nodes=list.map(item=>{
        const card=element('div','mw-native-accessory');card.dataset.accessoryProductId=item.id;
        const label=element('label'),input=element('input');input.type='checkbox';input.value=item.variantId;input.dataset.price=String(this.price(item).amount);input.dataset.accessoryProductId=item.id;input.setAttribute('aria-label',item.title);
        const disabled=!item.available||item.status==='DRAFT'||this.price(item).currency!==this.currency;
        input.disabled=disabled;label.append(input);if(item.image)label.append(this.picture(item));
        card.append(label,this.link(item,item.title.replace(/ \| Masuria(?: Wild)?$/,'')),element('strong',null,this.money(item)));
        if(disabled)card.append(element('span','mw-native-accessory__availability',item.status==='DRAFT'?this.labels.draft:this.labels.unavailable));
        return card;
      });
      track.replaceChildren(...nodes);
      const count=component.querySelector('[data-accessory-count]');if(count)count.textContent=String(list.length);
      component.dataset.basePrice=String(this.price(this.current).amount);
      component.updateTotal?.();
    }
    renderDescription() {
      if(!this.preview||this.current.status!=='DRAFT')return;
      const title=this.root.querySelector('.product-single__title');
      if(title)title.textContent=this.current.title;
      this.root.dataset.productTitle=this.current.title;
      const saved=this.read('[data-mw-description]',{})[this.current.id] || {},description=this.root.querySelector('[data-product-description-inner]');
      if(description&&saved[this.language])description.innerHTML=saved[this.language];

      for(const spec of this.root.querySelectorAll('[data-mw-spec-key]')){
        const value=this.current.specs[spec.dataset.mwSpecKey];if(!value)continue;
        for(const child of [...spec.children])if(child.tagName!=='STRONG')child.remove();
        spec.append(element('span',null,value));
      }
      for(const link of this.root.querySelectorAll('.product-block--desc a[href]')){
        const handle=new URL(link.href,location.origin).pathname.split('/products/')[1];
        const target=Object.values(this.data).find(item=>item.handle===handle);
        if(target?.status==='DRAFT'){link.href=this.href(target);link.removeAttribute('target');}
      }
    }
    refresh() {
      if(!this.initialized)return;
      for(const section of document.querySelectorAll('[data-mw-compare-product-id="'+this.current.id+'"]')){
        if(section.dataset.mwRendered==='true')continue;
        const other=this.data[this.current.compare];if(!other)continue;
        const grid=section.querySelector('.product-compare__grid');if(!grid)continue;
        const base = this.items(this.current.contents).find(item => item.model === this.current.model && !item.contents.length) || this.current;
        grid.replaceChildren(this.compareCard(base,true,base.id !== this.current.id),this.compareCard(other,false));
        section.hidden=false;section.dataset.mwRendered='true';
      }
    }
    compareCard(item,current,selectedModel = false) {
      const card=element('div','product-compare__card product-compare__card--'+(current?'current':'link'));card.dataset.productId=item.id;
      if(current)card.append(element('span','product-compare__badge',selectedModel?this.labels.current_model:this.labels.current_badge));
      const hero=element('div','product-compare__hero'),heroLink=current?element('div','product-compare__hero-link'):this.link(item,undefined,'product-compare__hero-link');
      const frame=element('div','product-compare__hero-image'+(item.compareImage?' product-compare__hero-image--compare':''));
      if(item.image||item.compareImage)frame.append(this.picture(item,item.compareImage||item.image));
      const caption=element('div','product-compare__hero-caption');caption.append(element('h3','product-compare__title',item.short),element('p','product-compare__price',(this.labels.from||'__PRICE__').replace('__PRICE__',this.money(item))));
      heroLink.append(frame,caption);hero.append(heroLink);card.append(hero);
      const specs=element('dl','product-compare__specs');
      const row=(key,value)=>{const box=element('div','product-compare__spec'+(key==='colors'?' product-compare__spec--colors':''));box.append(element('dt',null,this.labels[key]));const dd=element('dd');if(typeof value==='string')dd.textContent=value;else dd.append(value);box.append(dd);specs.append(box);};
      for(const [label,key]of [['best_for','purpose'],['pit_size','pit'],['weight','weight'],['smokeless','smokeless'],['fuel','power_source'],['regulated','regulated']])if(item.specs[key])row(label,item.specs[key]);
      const colors=element('div','product-compare__colors');
      for(const variant of this.items(item.colors)){const link=this.link(variant,undefined,'product-compare__color product-compare__color--link');link.append(this.swatch(variant,'color-swatch product-compare__color-swatch'),element('span','product-compare__color-label',variant.color));colors.append(link);}
      if(colors.childElementCount)row('colors',colors);
      const accessories=element('div','product-compare__accessories');
      for(const accessory of this.items([...item.accessories, ...item.bonus])){
        const link=this.link(accessory,undefined,'product-compare__accessory');link.dataset.accessoryProductId=accessory.id;
        const image=element('span','product-compare__accessory-image');if(accessory.image)image.append(this.picture(accessory));
        link.append(image,element('span','product-compare__accessory-title',accessory.short.replace(/ \| Masuria(?: Wild)?$/,'')));accessories.append(link);
      }
      row('accessories',accessories.childElementCount?accessories:'—');
      if(item.specs.warranty)row('warranty',item.specs.warranty);card.append(specs);
      const actions=element('div','product-compare__actions');actions.append(current?element('span','btn btn--secondary product-compare__btn product-compare__btn--current',selectedModel?this.labels.current_model:this.labels.current_product):this.link(item,this.labels.check_out,'btn product-compare__btn'));card.append(actions);
      return card;
    }
  }
  customElements.define('mw-pilot-preview',ProductCard);
  if(!customElements.get('mw-compare-mount'))customElements.define('mw-compare-mount',class extends HTMLElement{
    connectedCallback(){queueMicrotask(()=>this.dispatchEvent(new CustomEvent('mw:compare-ready',{bubbles:true})));}
  });
})();




